'use strict';
import {LOAD} from 'redux-storage';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/merge';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/toPromise';
import {geolocation} from '../../_libs/rx-dom';
import {GpsStatus} from '../../core-utilities/enums/GpsStatus';
import {epicErrorHandler} from '../../core-utilities/rx-utils';
import {reverseGeoCode} from '../../core-utilities/geo/GeoCoder';
import {gpsSupported} from '../../core-utilities-implementation/device-services/DeviceServicesCordova';
import {firebaseAuthSuccess} from '../../auth/actions';
import {getIsOnline, getTrackLocation} from '../reducers/state';
import * as trackLocationActions from '../actions/trackLocationActions';
import {logger} from './logger';


const CACHE_DURATION = 5 * 60 * 1000;

const GPS_ACCURACY = {
  enableHighAccuracy: true,
  timeout: 90 * 1000,
  maximumAge: 0, // 5000,
};

export class TrackLocationEpics {
  constructor() {
    this.gpsSupported = gpsSupported;
    this.geolocation = geolocation;
    this.reverseGeoCode = reverseGeoCode;
    this.cacheDuration = CACHE_DURATION;
    this.logger = logger;

    this.onAppReload = this.onAppReload.bind(this);
    this.onTrackLocation = this.onTrackLocation.bind(this);
    this.onRegister = this.onRegister.bind(this);
    this.onDeregister = this.onDeregister.bind(this);

    this.epics = [
      this.onAppReload,
      this.onTrackLocation,
      this.onRegister,
      this.onDeregister,
    ];
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onAppReload(action$, store) {
    return Observable
      .combineLatest(
        action$.ofType(firebaseAuthSuccess.TYPE),
        action$.ofType(LOAD),
      )
      .filter(() => this.gpsSupported() && getTrackLocation(store.getState()).size > 0)
      .mapTo(trackLocationActions.startTracking())
      .catch(epicErrorHandler(this.logger, 'onAppReload'));
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onRegister(action$, store) {
    return action$.ofType(trackLocationActions.registerTracking.TYPE)
      .filter(() => this.gpsSupported() && getTrackLocation(store.getState()).gpsStatus === GpsStatus.STOPPED)
      .mapTo(trackLocationActions.startTracking())
      .catch(epicErrorHandler(this.logger, 'onRegister'));
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onDeregister(action$, store) {
    return action$.ofType(trackLocationActions.deregisterTracking.TYPE, trackLocationActions.updatePosition.TYPE)
      .filter(() => {
        const state = getTrackLocation(store.getState());
        return state.gpsStatus !== GpsStatus.STOPPED && state.size === 0;
      })
      .mapTo(trackLocationActions.stopTracking())
      .merge(Observable.timer(this.cacheDuration)
        .take(1)
        .mapTo(trackLocationActions.resetTrackingCache())
        .takeUntil(action$.ofType(trackLocationActions.updatePosition.TYPE))
      )
      .catch(epicErrorHandler(this.logger, 'onDeregister'));
  }

  /**
   * TODO: finish reviewing this epic. should only work in an on-off single action flow now
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onTrackLocation(action$, store) {

    const stopTimer$ = action$.ofType(trackLocationActions.stopTracking.TYPE)
      .mapTo(trackLocationActions.updatePosition({
        gpsStatus: GpsStatus.STOPPED,
        timestamp: new Date().getTime(),
      }));

    return action$.ofType(trackLocationActions.startTracking.TYPE)
      .switchMap(() =>
        Observable.merge(
          // if the timer is stopped before a location is retrieved
          stopTimer$,
          this.geolocation
            .watchPosition(GPS_ACCURACY)
            .switchMap(event => {
              if (!event.isError) {
                const position = event.position;
                // iOS / Safari returns a float instead of an integer for some reason.
                const timestamp = Math.floor(position.timestamp);
                const isOnline = getIsOnline(store.getState());
                if (!isOnline) {
                  // Skip attempting to resolve address if offline.
                  return Observable.of(trackLocationActions.updatePosition({
                    gpsStatus: GpsStatus.WATCHING,
                    timestamp,
                    position,
                  }));
                } else {
                  // use a maximum timeout of 30 seconds
                  return this.reverseGeoCode(position.coords.latitude, position.coords.longitude, 30000)
                    .map(location => trackLocationActions.updatePosition({
                      gpsStatus: GpsStatus.WATCHING,
                      timestamp,
                      position,
                      location,
                    }))
                    .catch(err => Observable.of(trackLocationActions.updatePosition({
                      gpsStatus: GpsStatus.WATCHING,
                      timestamp,
                      position,
                    })));
                }
              } else {
                return Observable.of(trackLocationActions.updatePosition({
                  gpsStatus: GpsStatus.WATCHING_ERROR,
                  timestamp: new Date().getTime(),
                  err: event.error,
                }));
              }
            })
            // TODO: we may want retry logic for unexpected errors. at least on desktop, give up on permission errors
            .catch(err => Observable.of(trackLocationActions.updatePosition({
              gpsStatus: GpsStatus.ERROR,
              timestamp: new Date().getTime(),
              err,
            })))
            .takeUntil(stopTimer$),
        )
      )
      .catch(epicErrorHandler(this.logger, 'onStartTimer'));
  }
}

const trackLocationEpics = new TrackLocationEpics();

export const epics = trackLocationEpics.epics;



// WEBPACK FOOTER //
// ./src/js/settings/epics/trackLocationEpics.js